import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { translations, SUPPORTED, DEFAULT_LOCALE } from "./translations";

/**
 * Lightweight i18n. React Context + a dictionary lookup by key, persisted
 * to localStorage. No bundle cost beyond a couple kB of strings — we don't
 * pull in i18next for a two-locale app.
 *
 * Missing keys fall back to the key itself, which is noisy enough to spot
 * in QA but never breaks the render.
 */

const STORAGE_KEY = "locale";

function detectInitial() {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED.includes(stored)) return stored;
  const nav = (window.navigator.language || "").slice(0, 2).toLowerCase();
  return SUPPORTED.includes(nav) ? nav : DEFAULT_LOCALE;
}

const I18nContext = createContext({
  lang: DEFAULT_LOCALE,
  t: (k) => k,
  setLang: () => {},
});

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectInitial);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // localStorage can throw in Safari private mode — not fatal.
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (SUPPORTED.includes(next)) setLangState(next);
  }, []);

  const value = useMemo(() => {
    const dict = translations[lang] || translations[DEFAULT_LOCALE];
    const t = (key, vars) => {
      let result = dict[key];
      if (result === undefined) {
        // Fall back to default locale before using the key itself, so one
        // missing translation in EN still shows the RU string rather than
        // the raw "catalog.hero.title" key.
        const fallback = translations[DEFAULT_LOCALE][key];
        result = fallback !== undefined ? fallback : key;
      }
      // {name} placeholders — keep it dumb on purpose (no nesting, no
      // pluralisation) so callers can't accidentally hit a parsing edge
      // case. Pluralisation belongs in dedicated helpers.
      if (vars && typeof result === "string") {
        result = result.replace(/\{(\w+)\}/g, (_, k) =>
          vars[k] !== undefined ? String(vars[k]) : `{${k}}`
        );
      }
      return result;
    };
    return { lang, setLang, t, supported: SUPPORTED };
  }, [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
